import { useQuery } from '@tanstack/react-query';
import { ONE_DAY_IN_MILLISECONDS, TWO_HOURS_IN_MILLISECONDS } from 'mdc-constants';
import { useTypedOpenApi } from '../../providers';
import { getCurrentUser } from '../users/currentUserApi';

/**
 * The key every reader and every invalidator shares.
 *
 * Deliberately *not* the `['getCurrentUserData']` the Lumen query used, unlike the settings
 * migration which kept its key. Two reasons the settings move did not have: the shape changed —
 * the user arrives flat where the query wrapped it in `authUser` — and mobile persists its query
 * cache under a buster that is only the app version. Reusing the key would let a cache written by
 * an older build be restored into code that reads the new shape, and every field would come back
 * undefined: a signed-in user rendering as nobody. A new key simply misses and refetches.
 */
export const CURRENT_USER_QUERY_KEY = ['currentUser'];

/**
 * How long the user is trusted for, shared by every observer of the key above.
 *
 * Long, because nothing in `UserResource` changes on its own: it is the name, the email, the ids
 * other services know the user by, the billing address and the card's last four. Every write that
 * touches one of them already forces the refresh itself — `UpdateAddressModal` invalidates this
 * key, and both `useRefreshAfterUpdate` hooks call `refetch()`, which ignores staleness — so the
 * only thing a short window buys is a refetch on the way into every screen that reads the user.
 *
 * Spread into `useMe` too. Two observers of one key with two different `staleTime`s is two
 * different answers to "should this refetch on mount", and the shorter one wins for both.
 */
export const CURRENT_USER_CACHE = {
  staleTime: TWO_HOURS_IN_MILLISECONDS,
  gcTime: ONE_DAY_IN_MILLISECONDS,
} as const;

/**
 * The signed-in user, from `GET /users/me`.
 *
 * Replaces the Lumen `getCurrentUserData` query, and carries identity only — see
 * `currentUserApi`. Anything the user has *set* is read from `useSettings` instead.
 *
 * No `placeholderData`, which is where this differs from `useUserSettings`. A unit or a date format
 * has an honest default to show while the request is in flight; an identity does not. A stand-in
 * `id` would be written into offline rows as their author and sync would reject them, so callers
 * see `undefined` until the real user arrives and decide for themselves what to do about it — see
 * the dashboard's `useCurrentUserId`, which keeps the last real id on disk for exactly this reason.
 */
export const useCurrentUser = () => {
  const { apiClient } = useTypedOpenApi();

  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: () => getCurrentUser(apiClient),
    ...CURRENT_USER_CACHE,
  });
};

export default useCurrentUser;
